<template>
  <v-card flat class="step-tab-panel">
    <v-row class="region__header ma-0 flex-grow-0">
      <v-col class="d-flex align-center">
        <span class="font-weight-bold mr-1">{{ $t('region.useRegions') }}</span>

        <DocsLink link="toolguide/140-regionalbrands.html" />
      </v-col>
    </v-row>

    <v-container fluid class="flex-grow-1 d-flex flex-column">
      <v-row class="flex-grow-0">
        <v-col class="pa-0 d-flex justify-end align-center">
          <v-select
            v-model="selectedAttribute"
            :items="attributes"
            :placeholder="tkey('attributeSelectionPlaceholder')"
            class="mr-2"
            style="max-width: 240px"
            item-title="name"
            item-value="name"
            hide-details
          />
          <v-btn
            id="btnGenerateRegions"
            primary
            data-id-e2e="btnGenerateRegions"
            class="m-2"
            :disabled="isGenerateRegionsDisabled"
            @click="generateRegions(false)"
          >
            {{ $t('region.generateRegions') }}
          </v-btn>
        </v-col>
      </v-row>

      <v-divider />

      <v-row
        class="flex-grow-1"
        :class="{ 'align-content-center': isGenerateRegionsByAttributeJobRunning }"
      >
        <ProgressBar
          v-if="isGenerateRegionsByAttributeJobRunning"
          class="mb-5"
          :message="$t('messages.inProgress')"
          :percentage="jobStatuses.regionsByAttributeGenerator.progress"
          :status="jobStatuses.regionsByAttributeGenerator.status"
        />

        <AgGridVue
          v-else
          class="w-100 h-100"
          :loading="isGridLoading"
          :column-defs="columnDefs"
          :row-data="regionData"
          :grid-options="gridOptions"
          stop-editing-when-cells-loses-focus
          @cell-value-changed="trackDiff"
          @grid-ready="onGridReady"
        />
      </v-row>
    </v-container>

    <div class="page-actions-container">
      <PageActions
        :save-disabled="isEditingDisabled || loading"
        :is-discard-enabled="!isEditingDisabled && !loading"
        :has-data-errors="false"
        :has-data-changes="hasDataChanges"
        @discard="discard"
        @save="onSave(false)"
      >
        <template #right-btns>
          <v-btn
            class="text-outline"
            secondary
            variant="flat"
            :disabled="hasSkippedRegion || isEditingDisabled || loading"
            @click="skipRegions(false)"
          >
            {{ $t('actions.skipRegion') }}
          </v-btn>
        </template>
      </PageActions>

      <DependencyTreeFeedbackModal
        :value="dependencyTreeModalOpen"
        :results="dependencyTreeFeedback"
        page="region"
        @close="closeDependencyTreeModal"
        @commit="commitHandler"
      />
    </div>
  </v-card>
</template>

<script>
import AgGridCellRendererSelect from '@/components/AgGridCellRendererSelect.vue';
import { useInternationalization } from '@/composables/use-internationalization';
import { usePermission } from '@/composables/use-permission';
import colours from '@/plugins/ow-colors';
import { useContextStore } from '@/stores/context';
import { useDependencyTreeStore } from '@/stores/dependency-tree';
import { useScenariosStore } from '@/stores/scenarios';
import { useProductRegionsStore } from '@/stores/product-regions';
import agGridUtils from '@/utils/ag-grid-utils';
import regionClassifications from '@enums/region-classifications';
import {
  capitalize,
  cloneDeep,
  floor,
  get,
  isEmpty,
  isEqual,
  isUndefined,
  keyBy,
  map,
  max,
  min,
  reduce,
  size,
  values,
} from 'lodash';
import { mapActions, mapState } from 'pinia';

export default {
  components: {
    /* eslint-disable vue/no-unused-components */
    AgGridCellRendererSelect,
  },

  props: {
    disabled: {
      required: false,
      default: false,
      type: Boolean,
    },
  },

  setup() {
    const { canEditRegionPage } = usePermission();
    const { tkey } = useInternationalization('region');
    return { canEditRegionPage, tkey };
  },

  data() {
    return {
      isGridLoading: false, // controls grid overlay
      gridApi: null,
      gridOptions: {
        columnMenu: 'legacy',
        headerHeight: 40,
        rowHeight: 40,
        suppressContextMenu: true,
        defaultColDef: {
          filter: true,
          sortable: true,
          comparator: agGridUtils.sortings.naturalSort,
          editable: false,
          menuTabs: ['filterMenuTab'],
          minWidth: 100,
          flex: 1,
          suppressMovable: true,
        },
        // hides blue border
        suppressCellFocus: true,
        rowSelection: { enableClickSelection: true },
      },
      selectedAttribute: null,
      regionData: null,
      currentStateDiff: {},
      dependencyTreeFeedback: {},
      dependencyTreeModalOpen: false,
      calledFromGenerateRegionsClick: false,
      calledFromSkipRegionsClick: false,
    };
  },

  computed: {
    ...mapState(useProductRegionsStore, ['regions', 'loading']),
    ...mapState(useContextStore, ['clientConfig', 'hasPermission']),
    ...mapState(useScenariosStore, ['selectedScenario', 'jobStatuses', 'isJobRunning']),

    hasSkippedRegion() {
      return !!get(this.selectedScenario, 'regionSettings.hasSkippedRegionsByAttribute');
    },

    hasDataChanges() {
      return !isEmpty(this.currentStateDiff);
    },

    isGenerateRegionsByAttributeJobRunning() {
      return this.isJobRunning('regionsByAttributeGenerator');
    },

    isEditingDisabled() {
      return this.isGenerateRegionsByAttributeJobRunning || !this.canEditRegionPage;
    },

    isGenerateRegionsDisabled() {
      return this.isEditingDisabled || this.loading || !this.selectedAttribute;
    },

    columnDefs() {
      return [
        {
          field: 'attributeValue',
          colId: 'attributeValue',
          headerName: get(
            this.selectedScenario,
            'regionSettings.attribute',
            this.tkey('columns.attribute')
          ),
        },
        {
          field: 'productCount',
          colId: 'productCount',
          headerName: this.tkey('columns.products'),
          maxWidth: 250,
        },
        {
          field: 'storeCount',
          colId: 'storeCount',
          headerName: this.tkey('columns.stores'),
          cellStyle: params => {
            const colourIndex = this.getColourIndex(params.value);
            return {
              color: colours.regionStoreCountColours.colour[colourIndex],
              backgroundColor: colours.regionStoreCountColours.background[colourIndex],
            };
          },
          maxWidth: 250,
        },
        {
          field: 'classification',
          colId: 'classification',
          headerName: this.tkey('columns.classification'),
          filterParams: { valueFormatter: params => capitalize(params.value) },
          cellRenderer: 'AgGridCellRendererSelect',
          cellRendererParams: () => ({
            items: reduce(
              regionClassifications,
              (acc, c) => {
                acc.push({ text: capitalize(c), value: c });
                return acc;
              },
              []
            ),
            isDisplayed: true,
            isDisabled: this.isEditingDisabled,
            maxWidth: '400px',
          }),
          maxWidth: 400,
        },
      ];
    },

    attributes() {
      return this.selectedScenario.customAttributes;
    },

    storeCountRanges() {
      const storeCounts = map(this.regions, 'storeCount');
      return { min: min(storeCounts) || 0, max: max(storeCounts) || 0 };
    },
  },

  watch: {
    regions: {
      deep: true,
      handler(newValue, oldValue) {
        if (!this.gridApi || isEqual(newValue, oldValue)) return;
        this.resetRegions();
      },
    },
  },

  async created() {
    this.selectedAttribute = get(this.selectedScenario, 'regionSettings.attribute', null);
    await this.fetchRegions();
    this.resetRegions();
  },

  methods: {
    ...mapActions(useProductRegionsStore, [
      'setLoading',
      'fetchRegions',
      'updateRegions',
      'generateRegionsByAttributeJobComplete',
    ]),
    ...mapActions(useDependencyTreeStore, ['triggerDependencyTree']),
    ...mapActions(useScenariosStore, [
      'updateScenario',
      'refreshScenario',
      'setScenarioHasSkippedRegionsByAttribute',
      'runGenerateRegionsByAttribute',
    ]),

    onGridReady(params) {
      this.gridApi = params.api;
    },

    async generateRegions(commit = false) {
      this.calledFromGenerateRegionsClick = true; // for dependency tree modal

      // Perform dependency tree check
      const results = await this.triggerDependencyTree({
        params: {
          change: 'regionsModified',
          updates: { global: { selectedAttribute: this.selectedAttribute } },
          commit,
        },
      });
      if (results.needsFeedback) {
        this.dependencyTreeFeedback = results.output;
        this.dependencyTreeModalOpen = true;
        return;
      }

      // Save the region settings before triggering the job
      const regionSettingsUpdates = {
        id: this.selectedScenario._id,
        'regionSettings.attribute': this.selectedAttribute,
        'regionSettings.regionRules': this.clientConfig.regionRules,
      };
      await this.updateRegionSettings(regionSettingsUpdates);

      await this.runGenerateRegionsByAttribute();
    },

    resetRegions() {
      this.isGridLoading = false;
      this.currentStateDiff = {};
      // Reset before setting table data to ensure that saved state is available
      this.$options.savedState = cloneDeep(keyBy(this.regions, '_id'));
      this.regionData = cloneDeep(this.regions);
      this.isGridLoading = false;
    },

    getColourIndex(count) {
      const totalColours = size(colours.regionStoreCountColours.background);
      if (count <= this.storeCountRanges.min) return 0;
      if (count >= this.storeCountRanges.max) return totalColours - 1;
      const percentage =
        (count - this.storeCountRanges.min) /
        (this.storeCountRanges.max - this.storeCountRanges.min);
      const colourIndex = floor(percentage * totalColours);
      // Ensure index is always less than the total colours
      return colourIndex < totalColours ? colourIndex : totalColours - 1;
    },

    trackDiff(params) {
      const id = params.data._id;
      const { field } = params.colDef;
      this.updateDiff(id, field, params.value);
    },

    updateDiff(id, field, currentValue) {
      const path = `${id}.${field}`;
      const originalValue = get(this.$options.savedState, path);
      const existingValue = get(this.currentStateDiff, path);
      const valueHasChanged = currentValue !== originalValue;

      if (valueHasChanged) {
        if (!this.currentStateDiff[id]) this.currentStateDiff[id] = {};
        this.currentStateDiff[id][field] = currentValue;
      } else if (!isUndefined(existingValue)) {
        // Remove the field from the object if it's back to its old value.
        this.$delete(this.currentStateDiff[id], field);
        if (isEmpty(this.currentStateDiff[id])) {
          // If there's nothing left in the object, remove it.
          this.$delete(this.currentStateDiff, id);
        }
      }
    },

    hasDiff(params) {
      const { _id } = params.data;
      const { field } = params.colDef;
      const path = `${_id}.${field}`;

      const currentValue = get(params.data, field);
      const originalValue = get(this.$options.savedState, path);
      return agGridUtils.comparators.didValueChange(currentValue, originalValue);
    },

    formatRegionData() {
      return map(this.currentStateDiff, (update, key) => ({ _id: key, ...update }));
    },

    async onSave(commit = false) {
      // Perform dependency tree check
      const results = await this.triggerDependencyTree({
        params: {
          change: 'regionsModified',
          updates: { rowLevel: this.formatRegionData() },
          commit,
        },
      });

      if (results.needsFeedback) {
        this.dependencyTreeFeedback = results.output;
        this.dependencyTreeModalOpen = true;
        return;
      }

      await this.setScenarioHasSkippedRegionsByAttribute({ hasSkippedRegionsByAttribute: false });
      this.saveChanges();
    },

    async saveChanges() {
      this.isGridLoading = true;

      const regionUpdates = this.formatRegionData();
      await this.updateRegions(regionUpdates);

      this.isGridLoading = false;
    },

    async skipRegions(commit = false) {
      this.calledFromSkipRegionsClick = true;

      // Perform dependency tree check
      const results = await this.triggerDependencyTree({
        params: {
          change: 'regionsModified',
          updates: {
            // Unset selected attribute to trigger deletion of region data
            global: { selectedAttribute: null },
          },
          commit,
        },
      });
      if (results.needsFeedback) {
        this.dependencyTreeFeedback = results.output;
        this.dependencyTreeModalOpen = true;
        return;
      }
      this.selectedAttribute = null;
      // Reset region settings in scenario
      const regionSettingsUpdates = {
        id: this.selectedScenario._id,
        'regionSettings.hasSkippedRegionsByAttribute': true,
        'regionSettings.attribute': this.selectedAttribute,
        'regionSettings.regionRules': [],
      };
      await this.updateRegionSettings(regionSettingsUpdates);
      // Reset region table
      await this.fetchRegions();
      this.resetRegions();
    },

    async updateRegionSettings(updates) {
      this.setLoading(true);
      await this.updateScenario({ scenario: updates });
      await this.refreshScenario();
    },

    discard() {
      this.currentStateDiff = {};
      this.regionData = cloneDeep(values(this.$options.savedState));
    },

    closeDependencyTreeModal() {
      this.dependencyTreeModalOpen = false;
      this.dependencyTreeFeedback = {};
    },

    async commitHandler() {
      if (this.calledFromSkipRegionsClick) await this.skipRegions(true);
      else if (this.calledFromGenerateRegionsClick) await this.generateRegions(true);
      else this.onSave(true);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.region {
  &__header {
    border-bottom: 1px solid $assortment-panel-border-divider-colour;
  }
}

.page-actions-container {
  border-top: 1px solid $assortment-panel-border-divider-colour;
}

:deep(.ag-cell-range-right.ag-cell-inline-editing) {
  border: none !important;
}

:deep(.ag-cell:focus) {
  border: none !important;
  outline: none !important;
}

:deep(.ag-cell-range-single-cell) {
  border: none !important;
}

:deep(.ag-cell[col-id='classification']) {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: 16px;
}
</style>
